import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Calendar,
    ClipboardList,
    MessageSquare,
    UserCheck,
    Video,
} from "lucide-react";
import { Badge } from "@/components/ui/badge.tsx";
import { SectionTitle, BlockTitle } from "@/components/ui/headings";
import { queryKeys } from "@/services/queryKeyFactory.ts";
import {
    getMentorMenteesWithSessions,
    MentorSessionForList,
    MentorMenteesWithSessionsResponse,
} from "@/services/mentorService.ts";
import { getMenteeSessions } from "@/services/menteeService.ts";
import { Mentor, MentorWithoutAssociations } from "@/types/Mentor.ts";
import { MentorAvailability} from "@/types/MentorAvailability.ts";
import { timezoneResolver } from "@/utils/time-helper.ts";
import { activeLocale, LocalizedField } from "@/utils/translation-helper.ts";
import {
    adjustTimezone,
    convertTo12Hour,
    formatSessionTime12h,
    getDate,
    getDayOfWeek,
} from "@/utils/time-helper.ts";

type SessionWithMeta = MentorSessionForList & {
    isPast: boolean;
    attendanceRecorded: boolean;
    attendancePct: number;
};

function useSessionsWithMeta(
    mentorId: number,
    enabled: boolean
): {
    sessions: SessionWithMeta[];
    nextMeeting: SessionWithMeta | null;
    isLoading: boolean;
} {
    const { data, isLoading } = useQuery({
        queryKey: queryKeys.fetchMentors.menteesWithSessions(mentorId),
        queryFn: () => getMentorMenteesWithSessions(mentorId),
        enabled,
    });

    if (!data || isLoading) {
        return { sessions: [], nextMeeting: null, isLoading };
    }

    const now = new Date();
    const sessionEndById = buildSessionEndById(data);
    const attendanceBySessionId = buildAttendanceBySessionId(data);

    const sessions: SessionWithMeta[] = data.sessions.map((s) => {
        const end = sessionEndById.get(s.sessionId);
        const isPast = end != null && end < now;
        const att = attendanceBySessionId.get(s.sessionId) ?? {
            recorded: false,
            pct: 0,
        };
        return {
            ...s,
            isPast,
            attendanceRecorded: att.recorded,
            attendancePct: att.pct,
        };
    });

    const nextMeeting =
        sessions.find((s) => !s.isPast) ?? null;

    return { sessions, nextMeeting, isLoading: false };
}

function buildSessionEndById(
    data: MentorMenteesWithSessionsResponse
): Map<number, Date> {
    const map = new Map<number, Date>();
    for (const s of data.sessions) {
        const endTimePart = s.sessionTime.split(/ [–-] /).pop()?.trim();
        if (!endTimePart) continue;
        const [h, m] = endTimePart.split(":").map(Number);
        const date = new Date(s.sessionDate + "T12:00:00");
        date.setHours(h, m ?? 0, 0, 0);
        map.set(s.sessionId, date);
    }
    return map;
}

function buildAttendanceBySessionId(
    data: MentorMenteesWithSessionsResponse
): Map<number, { recorded: boolean; pct: number }> {
    const map = new Map<number, { recorded: boolean; pct: number }>();
    for (const session of data.sessions) {
        let attended = 0;
        let missed = 0;
        for (const mentee of data.mentees) {
            const st = mentee.sessionStatuses.find(
                (ss) => ss.sessionId === session.sessionId
            );
            if (st?.status === "attended") attended++;
            else if (st?.status === "missed") missed++;
        }
        const total = attended + missed;
        map.set(session.sessionId, {
            recorded: total > 0,
            pct: total > 0 ? Math.round((attended / total) * 100) : 0,
        });
    }
    return map;
}

type MentorSessionsBlockProps = {
    mentor: Mentor | MentorWithoutAssociations;
    menteeId?: number; // If provided, renders mentee view
};

export default function MentorSessionsBlock({ mentor, menteeId }: MentorSessionsBlockProps) {
    const { t } = useTranslation();
    const mentorId = mentor.id;
    const isMenteeView = menteeId != null;
    
    // Mentee sessions query
    const { data: menteeSessionsData, isLoading: menteeSessionsLoading } = useQuery({
        queryKey: queryKeys.fetchMentees.sessions(menteeId!),
        queryFn: () => getMenteeSessions(menteeId!),
        enabled: isMenteeView && menteeId! > 0,
    });

    // Mentor sessions query
    const hasSessionsApi = true;
    const {
        sessions: sessionsFromApi,
        isLoading: sessionsLoading,
    } = useSessionsWithMeta(mentorId, hasSessionsApi && !isMenteeView);

    const availabilities = mentor.availabilities ?? [];
    const now = new Date();
    const [pastAvail, upcomingAvail] = availabilities.reduce<
        [MentorAvailability[], MentorAvailability[]]
    >(
        ([past, up], a) => {
            const d = adjustTimezone(a.availableOn);
            if (d < now) past.push(a);
            else up.push(a);
            return [past, up];
        },
        [[], []]
    );
    pastAvail.sort(
        (a, b) =>
            adjustTimezone(b.availableOn).getTime() -
            adjustTimezone(a.availableOn).getTime()
    );
    upcomingAvail.sort(
        (a, b) =>
            adjustTimezone(a.availableOn).getTime() -
            adjustTimezone(b.availableOn).getTime()
    );

    const useApiSessions = sessionsFromApi.length > 0 && !sessionsLoading;
    const upcomingSessions: SessionWithMeta[] = useApiSessions
        ? sessionsFromApi.filter((s) => !s.isPast)
        : [];
    const pastSessions: SessionWithMeta[] = useApiSessions
        ? sessionsFromApi.filter((s) => s.isPast)
        : [];
    
    // Mentee view data
    const menteeUpcoming = menteeSessionsData?.upcoming ?? [];
    const menteeAttended = menteeSessionsData?.attended ?? [];
    const menteeMissed = menteeSessionsData?.missed ?? [];
    
    const meetingLink = mentor.personalMeetingLink?.trim() || null;
    const timezoneLabel = mentor.timezone
        ? timezoneResolver[mentor.timezone]?.[activeLocale() as keyof LocalizedField]
        : null;
    
    const isLoading = isMenteeView ? menteeSessionsLoading : sessionsLoading;

    return (
        <div>
            <div className="flex flex-wrap items-center gap-3">
                <SectionTitle>
                    {t("dashboard.mentorshipSessions", "Mentorship Sessions")}
                </SectionTitle>
                {mentor.isOnWhatsApp && (
                    <Badge variant="success">
                        {t("mentor.availableOnWhatsApp")}
                    </Badge>
                )}
            </div>
            {(timezoneLabel || meetingLink) && (
                <p className="mt-2 text-sm text-text-2">
                    {timezoneLabel && (
                        <span className="mr-2">
                            {t("dashboard.allTimesIn", "All times in")}{" "}
                            {timezoneLabel}.
                        </span>
                    )}
                    {meetingLink && (
                        <span>
                            {t("dashboard.meetingLink", "Meeting link")}{" "}
                            <a
                                className="text-text-1 underline-offset-4 hover:underline"
                                href={meetingLink}
                            >
                                {meetingLink}
                            </a>
                        </span>
                    )}
                </p>
            )}

            <div className="mt-6 w-full space-y-6">
                    {isMenteeView ? (
                        <>
                            {isLoading ? (
                                <p className="text-sm text-text-2">{t("common.loading", "Loading...")}</p>
                            ) : (
                                <>
                                    {menteeUpcoming.length > 0 && (
                                        <section>
                                            <BlockTitle className="text-sm font-semibold uppercase tracking-wide text-text-2">
                                                {t("dashboard.upcomingMeetings", "Upcoming Meetings")}
                                            </BlockTitle>
                                            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                                                {menteeUpcoming.map((s, idx) => (
                                                    <SessionCard
                                                        key={s.sessionId}
                                                        session={s}
                                                        meetingLink={meetingLink}
                                                        isPast={false}
                                                        isNextMeeting={idx === 0}
                                                    />
                                                ))}
                                            </div>
                                        </section>
                                    )}
                                    {(menteeAttended.length > 0 || menteeMissed.length > 0) && (
                                        <section>
                                            <BlockTitle className="text-sm font-semibold uppercase tracking-wide text-text-2">
                                                {t("dashboard.pastMeetings", "Past Meetings")}
                                            </BlockTitle>
                                            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                                                {[...menteeAttended, ...menteeMissed]
                                                    .sort((a, b) => {
                                                        const dateCompare = b.sessionDate.localeCompare(a.sessionDate);
                                                        if (dateCompare !== 0) return dateCompare;
                                                        return b.sessionTime.localeCompare(a.sessionTime);
                                                    })
                                                    .map((s) => (
                                                        <SessionCard
                                                            key={s.sessionId}
                                                            session={{
                                                                ...s,
                                                                status: menteeAttended.some((a) => a.sessionId === s.sessionId)
                                                                    ? "attended"
                                                                    : "missed",
                                                            }}
                                                            meetingLink={null}
                                                            isPast
                                                            isNextMeeting={false}
                                                        />
                                                    ))}
                                            </div>
                                        </section>
                                    )}
                                    {menteeUpcoming.length === 0 && menteeAttended.length === 0 && menteeMissed.length === 0 && (
                                        <p className="text-sm text-text-2">
                                            {t("dashboard.noSessionsScheduled", "No sessions scheduled.")}
                                        </p>
                                    )}
                                </>
                            )}
                        </>
                    ) : useApiSessions ? (
                        <>
                            {upcomingSessions.length > 0 && (
                                <section>
                                    <BlockTitle className="text-sm font-semibold uppercase tracking-wide text-text-2">
                                        {t("dashboard.upcomingMeetings", "Upcoming Meetings")}
                                    </BlockTitle>
                                    <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        {upcomingSessions.map((s, idx) => (
                                            <SessionCard
                                                key={s.sessionId}
                                                session={s}
                                                meetingLink={meetingLink}
                                                isPast={false}
                                                isNextMeeting={idx === 0}
                                            />
                                        ))}
                                    </div>
                                </section>
                            )}
                            {pastSessions.length > 0 && (
                                <section>
                                    <BlockTitle className="text-sm font-semibold uppercase tracking-wide text-text-2">
                                        {t("dashboard.pastMeetings", "Past Meetings")}
                                    </BlockTitle>
                                    <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        {pastSessions.map((s) => (
                                            <SessionCard
                                                key={s.sessionId}
                                                session={s}
                                                meetingLink={null}
                                                isPast
                                                isNextMeeting={false}
                                            />
                                        ))}
                                    </div>
                                </section>
                            )}
                        </>
                    ) : (
                        <>
                            {upcomingAvail.length > 0 && (
                                <section>
                                    <BlockTitle className="text-sm font-semibold uppercase tracking-wide text-text-2">
                                        {t("dashboard.upcomingMeetings", "Upcoming Meetings")}
                                    </BlockTitle>
                                    <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        {upcomingAvail.map((a, idx) => (
                                            <AvailabilityCard
                                                key={idx}
                                                availability={a}
                                                isNext={idx === 0}
                                                meetingLink={meetingLink}
                                            />
                                        ))}
                                    </div>
                                </section>
                            )}
                            {pastAvail.length > 0 && (
                                <section>
                                    <BlockTitle className="text-sm font-semibold uppercase tracking-wide text-text-2">
                                        {t("dashboard.pastMeetings", "Past Meetings")}
                                    </BlockTitle>
                                    <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        {pastAvail.map((a, idx) => (
                                            <AvailabilityCard
                                                key={idx}
                                                availability={a}
                                                isNext={false}
                                                meetingLink={null}
                                            />
                                        ))}
                                    </div>
                                </section>
                            )}
                        </>
                    )}

                    {!useApiSessions && upcomingAvail.length === 0 && pastAvail.length === 0 && (
                        <p className="text-sm text-text-2">
                            {t("dashboard.noSessionsScheduled", "No sessions scheduled.")}
                        </p>
                    )}
                </div>
        </div>
    );
}

type SessionCardStatus = "attended" | "missed";

type SessionCardSession = {
    sessionId: number;
    sessionDate: string;
    sessionTime: string;
    hasMentorNote?: boolean;
    hasMenteeFeedback?: boolean;
    attendanceRecorded?: boolean;
    attendancePct?: number;
    status?: SessionCardStatus;
};

function SessionCard({
    session,
    meetingLink,
    isPast,
    isNextMeeting,
}: {
    session: SessionCardSession;
    meetingLink: string | null;
    isPast: boolean;
    isNextMeeting: boolean;
}) {
    const { t } = useTranslation();
    const dateLabel = getDate(new Date(session.sessionDate + "T12:00:00"));
    const dayLabel = getDayOfWeek(new Date(session.sessionDate + "T12:00:00"));
    const attendanceUrl = `/session-attendance?sessionId=${session.sessionId}`;

    const cardClassName = `rounded-xl border bg-bg-card p-4 shadow-sm transition-colors flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 ${
        isNextMeeting
            ? "border-2 border-accent"
            : "border border-border hover:border-border/80"
    }`;

    const content = (
        <>
            <div className="min-w-0 flex-1">
                <p className="text-base font-semibold text-text-1">{dateLabel}</p>
                <p className="text-sm text-text-2">{dayLabel}</p>
                <p className="mt-1 text-sm font-medium text-text-1">
                    {formatSessionTime12h(session.sessionTime)}
                </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 sm:flex-nowrap">
                {isNextMeeting && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wide text-bg-card">
                        <Calendar className="h-3.5 w-3.5" />
                        {t("dashboard.comingUp", "Coming Up")}
                    </span>
                )}
                {isPast && session.status === "attended" && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-status-success/15 px-3 py-1 text-xs font-semibold text-status-success">
                        {t("dashboard.attended", "Attended")}
                    </span>
                )}
                {isPast && session.status === "missed" && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-status-danger/15 px-3 py-1 text-xs font-semibold text-status-danger">
                        {t("dashboard.missed", "Missed")}
                    </span>
                )}
                {isPast && session.status == null && (
                    <>
                        {!session.attendanceRecorded ? (
                            <span
                                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400"
                                title={t("sessionAttendance.markAttendance", "Mark attendance")}
                            >
                                <UserCheck className="h-4 w-4" />
                            </span>
                        ) : (
                            <span
                                className="shrink-0 rounded-lg bg-emerald-100 px-2 py-1 text-xs font-semibold tabular-nums text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
                                title={t("dashboard.attendancePct", "Attendance")}
                            >
                                {session.attendancePct ?? 0}%
                            </span>
                        )}
                    </>
                )}
                {isPast && session.hasMentorNote && (
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-600 dark:bg-sky-500/20 dark:text-sky-400">
                        <ClipboardList className="h-4 w-4" />
                    </span>
                )}
                {isPast && session.hasMenteeFeedback && (
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-600 dark:bg-violet-500/20 dark:text-violet-400">
                        <MessageSquare className="h-4 w-4" />
                    </span>
                )}
                {!isPast && isNextMeeting && meetingLink && (
                    <a
                        href={meetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent/20 text-accent hover:bg-accent/30 dark:bg-accent/25 dark:hover:bg-accent/35"
                        title={t("dashboard.joinMeeting", "Join meeting")}
                    >
                        <Video className="h-4 w-4" />
                    </a>
                )}
                {!isPast && !isNextMeeting && meetingLink && (
                    <a
                        href={meetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent/20 text-accent hover:bg-accent/30 dark:bg-accent/25 dark:hover:bg-accent/35"
                        title={t("dashboard.joinMeeting", "Join meeting")}
                    >
                        <Video className="h-4 w-4" />
                    </a>
                )}
            </div>
        </>
    );

    if (isPast) {
        return (
            <Link to={attendanceUrl} className={`block ${cardClassName} hover:bg-bg-elevated/50`}>
                {content}
            </Link>
        );
    }

    return <div className={cardClassName}>{content}</div>;
}

function AvailabilityCard({
    availability,
    isNext,
    meetingLink,
}: {
    availability: MentorAvailability;
    isNext: boolean;
    meetingLink: string | null;
}) {
    const { t } = useTranslation();
    const d = adjustTimezone(availability.availableOn);
    const dateLabel = getDate(d);
    const dayLabel = getDayOfWeek(d);
    const timeLabel = `${convertTo12Hour(availability.availableFrom)} – ${convertTo12Hour(availability.availableTill)}`;

    return (
        <div
            className={`rounded-xl bg-bg-card p-4 shadow-sm transition-colors flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 ${
                isNext ? "border-2 border-accent" : "border border-border hover:border-border/80"
            }`}
        >
            <div className="min-w-0 flex-1">
                <p className="text-base font-semibold text-text-1">{dateLabel}</p>
                <p className="text-sm text-text-2">{dayLabel}</p>
                <p className="mt-1 text-sm font-medium text-text-1">{timeLabel}</p>
            </div>
            {isNext && (
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wide text-bg-card">
                        <Calendar className="h-3.5 w-3.5" />
                        {t("dashboard.comingUp", "Coming Up")}
                    </span>
                    {meetingLink && (
                        <a
                            href={meetingLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent/20 text-accent hover:bg-accent/30"
                            title={t("dashboard.joinMeeting", "Join meeting")}
                        >
                            <Video className="h-4 w-4" />
                        </a>
                    )}
                </div>
            )}
        </div>
    );
}
